import { Checkbox as MuiCheckbox, CheckboxProps, styled, Stack, FormControlLabel, Typography, Box, css, SxProps, Theme } from "@mui/material";
import { ChangeEvent, FC, ReactNode } from "react";

import { useFormField } from "@/modules/irohana-components/elements/FormField";
import { button, state, text } from "@/modules/irohana-components/theme/colors";

export type TCheckboxProps = {
  label?: ReactNode;
  subLabel?: string;
  nonControl?: boolean;
  prefixLabel?: ReactNode;
  error?: boolean;
  labelColor?: string;
  subLabelColor?: string;
  alignStart?: boolean;
  labelSx?: SxProps<Theme>;
  formControlLabelSx?: SxProps<Theme>;
} & CheckboxProps;

export const Checkbox: FC<TCheckboxProps> = ({
  label,
  subLabel,
  nonControl = false,
  prefixLabel,
  error,
  labelColor = text.primary,
  subLabelColor = text.secondary,
  alignStart = true,
  labelSx,
  formControlLabelSx,
  ...rest
}) => {
  const { field, fieldState } = useFormField(nonControl);
  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    field?.onChange(e);
    field?.onBlur();
    rest.onChange && rest.onChange(e, e.target.checked);
  };

  const checked = !!rest.checked || !!field?.value;

  return (
    <CheckboxWrap alignStart={alignStart}>
      <FormControlLabel
        sx={formControlLabelSx}
        label={
          <Stack flexDirection="row" gap={0.5}>
            {prefixLabel && (
              <Typography variant="body14" color={text.primary}>
                {prefixLabel}
              </Typography>
            )}
            <Stack gap={0.5}>
              {label && (
                <Typography variant="body14" sx={labelSx} color={labelColor}>
                  {label}
                </Typography>
              )}
              {subLabel && (
                <Typography variant="cap12" color={subLabelColor}>
                  {subLabel}
                </Typography>
              )}
            </Stack>
          </Stack>
        }
        control={
          <StyledCheckbox error={Boolean(error) || Boolean(fieldState?.error)} {...rest} {...field} checked={checked} onChange={handleChange} />
        }
      />
    </CheckboxWrap>
  );
};

const CheckboxWrap = styled(Box, { shouldForwardProp: (prop) => prop !== "alignStart" })<{ alignStart: boolean }>`
  .MuiFormControlLabel-root {
    align-items: ${({ alignStart }) => (alignStart ? "flex-start" : "center")};
    gap: 12px;
    margin: 0;
  }
`;

const options = { shouldForwardProp: (propName: string) => !["error"].includes(propName) };

const StyledCheckbox = styled(MuiCheckbox, options)<{ error: boolean }>`
  width: 18px;
  height: 18px;
  ${({ error }) =>
    error
      ? css`
          color: ${state.error_1}!important;
        `
      : css`
          color: ${button.primary}!important;
        `};
  margin-top: 2px;
  &.Mui-disabled {
    color: ${button.disable}!important;
  }
`;
